import { Injectable } from '@angular/core';
import { FMFResponse as _FMFResponse } from '@interfaces';
import { select, Store } from '@ngrx/store';
import { EnvTypes, flowMapper } from '@shared/models';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import * as fromRootStore from '@store/index';
import { SetFMFTId } from '@store/index';
import { IFMFState } from '@store/reducers/fraud-micro-frontend/fmf.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectFMFState } from '@store/selectors/fraud-micro-frontend/fmf.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import {
  ClientService,
  ConsumerArgumentsType,
  ConsumerPayloadsType,
  IQueryParameters,
  QueryParamsService,
  SysLevels,
} from '@vanguard/fmf-common';
import {
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  skipWhile,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { LoggerCodes, LoggerService } from '../../modules/logger/logger';
import { WindowRef } from '../window-ref/window-ref.service';

export interface FMFResponse extends _FMFResponse {}

export interface FMFClient {
  startProfiling: (consumerArguments: ConsumerArgumentsType) => Promise<ConsumerPayloadsType>;
  getAllTokens: (args: ConsumerArgumentsType) => Promise<ConsumerPayloadsType>;
}

@Injectable({
  providedIn: 'root',
})
export class FMFService {
  private readonly triageState$: Observable<TriageState>;
  private readonly fmfState$: Observable<IFMFState>;
  private readonly clientService: ClientService;
  private readonly queryParamsService: QueryParamsService;
  private readonly LN_PAGE_ID: string = '19';

  private params!: IQueryParameters | { vgClientPoid: string; vgReferer: string };

  constructor(
    private readonly globalStore: Store<fromRootStore.State>,
    private readonly win: WindowRef,
    private readonly loggingSvc: LoggerService,
  ) {
    this.triageState$ = this.globalStore.pipe(select(selectTriageState));
    this.fmfState$ = this.globalStore.pipe(select(selectFMFState));

    this.clientService = new ClientService();
    this.queryParamsService = new QueryParamsService();
  }

  init(): Observable<void> {
    return this.triageState$.pipe(
      skipWhile((triageState) => !triageState.flowType),
      take(1),
      mergeMap(async (triageState) => {
        const tId = uuidv4();
        const flowType = flowMapper[triageState.flowType];

        this.globalStore.dispatch(SetFMFTId({ payload: { transactionId: tId } }));
        const env = getConfigClientFromRTE().isProduction
          ? EnvTypes.PRODUCTION
          : EnvTypes.DEVELOPMENT;
        this.params = {
          transactionId: tId,
          flowType,
          poid: '',
          userType: '',
          serviceName: 'CWP',
          vgClientPoid: '',
          vgReferer: env === EnvTypes.PRODUCTION ? document.referrer : '',
        };

        try {
          this.loggingSvc.info(
            {
              eventId: 'fmf_service__init-request',
              source: 'FMFService',
              message: 'Attach FMF Script Request',
              data: this.params,
            },
            LoggerCodes.GENERAL_SUCCESS,
            true,
          );

          const isSuccess = await this.attachFMFScript(this.params);
          if (isSuccess) {
            this.startProfiling().subscribe();
          }

          this.loggingSvc.info(
            {
              eventId: `fmf_service__init-${isSuccess ? 'success' : 'failure'}`,
              source: 'FMFService',
              message: `FMF script has${isSuccess ? '' : ' not'} attached successfully`,
              data: this.params,
            },
            isSuccess ? LoggerCodes.GENERAL_SUCCESS : LoggerCodes.FMF_SERVICE_FAILURE,
            true,
          );
        } catch (error) {
          this.loggingSvc.error(
            {
              eventId: 'fmf_service__init-error',
              source: 'FMFService',
              message: 'Error during ataching FMF script',
              data: this.params,
            },
            LoggerCodes.FMF_SERVICE_FAILURE,
          );
        }
      }),
    );
  }

  public getAllTokens(args: ConsumerArgumentsType): Observable<FMFResponse> {
    return this.fmfState$.pipe(
      take(1),
      switchMap((fmfState) => {
        const { transactionId } = fmfState;

        const emptyResult: Observable<FMFResponse> = of({
          action: args.recaptcha,
          tokens: {},
          transactionId,
        });

        try {
          if (this.win.nativeWindow.Cypress) {
            this.loggingSvc.info(
              {
                eventId: 'fmf_service__getAllTokens-cypress',
                source: 'FMFService',
                message: 'FMF is disabled during Cypress tests',
                data: { args, fmfTId: transactionId },
              },
              LoggerCodes.GENERAL_SUCCESS,
              true,
            );

            return emptyResult;
          }

          this.loggingSvc.info(
            {
              eventId: 'fmf_service__getAllTokens-request',
              source: 'FMFService',
              message: 'FMF retrieve tokens request',
              data: { args, fmfTId: transactionId },
            },
            LoggerCodes.GENERAL_SUCCESS,
            true,
          );

          const fmfClient = this.win.nativeWindow.fmfClient;

          if (!fmfClient) {
            this.loggingSvc.error(
              {
                eventId: 'fmf_service__missing-script',
                source: 'FMFService',
                message: 'FMF script is missing',
                data: { args, fmfTId: transactionId },
              },
              LoggerCodes.FMF_SERVICE_FAILURE,
            );

            return emptyResult;
          }

          return from(fmfClient.getAllTokens(args)).pipe(
            tap((tokens: ConsumerPayloadsType) => {
              this.loggingSvc.info(
                {
                  eventId: 'fmf_service__getAllTokens-success',
                  source: 'FMFService',
                  message: 'Retrieve tokens',
                  data: { args, tokens, fmfTId: transactionId },
                },
                LoggerCodes.GENERAL_SUCCESS,
                true,
              );
            }),
            map((tokens) => ({
              transactionId,
              tokens,
              action: args.recaptcha,
            })),
            catchError((error) => {
              this.loggingSvc.error(
                {
                  eventId: 'fmf_service__getAllTokens-error',
                  source: 'FMFService',
                  message: 'Error during retriving tokens',
                  data: { error, errorMessage: error.message, args, fmfTId: transactionId },
                },
                LoggerCodes.FMF_SERVICE_FAILURE,
              );
              return emptyResult;
            }),
          );
        } catch (error: any) {
          this.loggingSvc.error(
            {
              eventId: 'fmf_service__getAllTokens-error',
              source: 'FMFService',
              message: 'FMF General Error',
              data: { error, errorMessage: error.message, args, fmfTId: transactionId },
            },
            LoggerCodes.FMF_SERVICE_FAILURE,
          );
          return emptyResult;
        }
      }),
    );
  }

  private attachFMFScript(
    params: IQueryParameters | { vgClientPoid: string; vgReferer: string },
  ): Promise<boolean> {
    const config = getConfigClientFromRTE();
    const env = config.isProduction ? EnvTypes.PRODUCTION : EnvTypes.DEVELOPMENT;
    const url = config.fmfUrl;

    const { query } = this.queryParamsService.setQueryParamsAndString(params as any);
    const src = `${url}/fmf?${query}`;

    const sysLevel = {
      development: 'sat',
      production: 'prd',
    };

    return this.clientService.generateScript(
      <SysLevels>sysLevel[env],
      params as any,
      {
        src,
        defer: true,
        id: 'cwpFmfScript',
        scriptLoadErrorMessage: 'FMF script is not loaded',
      },
      3,
    );
  }

  public startProfiling(): Observable<ConsumerPayloadsType> {
    return this.fmfState$.pipe(
      take(1),
      switchMap((fmfState) => {
        const { transactionId } = fmfState;

        const emptyResult: Observable<ConsumerPayloadsType> = of({ ln: {} });

        try {
          if (this.win.nativeWindow.Cypress) {
            this.loggingSvc.info(
              {
                eventId: 'fmf_service__startProfiling-cypress',
                source: 'FMFService',
                message: 'FMF is disabled during Cypress tests',
                data: { fmfTId: transactionId },
              },
              LoggerCodes.GENERAL_SUCCESS,
              true,
            );

            return emptyResult;
          }

          const initLNConfig = {
            ln: {
              lnTrxCd: transactionId,
              pageId: this.LN_PAGE_ID,
            },
          };

          this.loggingSvc.info(
            {
              eventId: 'fmf_service__startProfiling-request',
              source: 'FMFService',
              message: 'FMF start profiling request',
              data: { fmfTId: transactionId, ln: initLNConfig.ln },
            },
            LoggerCodes.GENERAL_SUCCESS,
            true,
          );

          const fmfClient: FMFClient = this.win.nativeWindow.fmfClient;

          if (!fmfClient) {
            this.loggingSvc.error(
              {
                eventId: 'fmf_service__startProfiling_missing-script',
                source: 'FMFService',
                message: 'FMF script is missing',
                data: { fmfTId: transactionId, ln: initLNConfig.ln },
              },
              LoggerCodes.FMF_SERVICE_FAILURE,
            );

            return emptyResult;
          }

          return fmfClient.startProfiling(initLNConfig);
        } catch (error: any) {
          this.loggingSvc.error(
            {
              eventId: 'fmf_service__startProfiling-error',
              source: 'FMFService',
              message: 'FMF General Error',
              data: { error, errorMessage: error.message, fmfTId: transactionId },
            },
            LoggerCodes.FMF_SERVICE_FAILURE,
          );
          return emptyResult;
        }
      }),
    );
  }
}
